'use strict'

import range from 'lowscore/range.js'
import zip from 'lowscore/zip.js'
import createPriceEngine from 'root-packages/price-engine/index.js'
import CachedRoomPermutations from '../../../packages/accommodation/cachedRoomPermutations.js'
import simpleCache from 'root-core/cache/simpleCache.js'
import debugFactory from 'debug'
import filterSplice from 'root-core/utils/array/filterSplice.js'
import Hotels from '../../../packages/accommodation/hotels/index.js'
import clone from 'root-core/utils/object/clone.js'
import LocalDate from '@booking-services/localDate'
import parseUserSuppliedDate from './parseUserSuppliedDate.js'
import {PaymentSchedulePolicy} from 'root-packages/models/index.js'
import combinePaymentSchedule from 'root-packages/tropicsClient/combinePaymentSchedule.js'
import buildPartyForAllocation from 'root-packages/buildPartyForAllocation.js'
import moment from 'moment'
import {calculateAirfareParty} from 'root-packages/calculateAirfareParty.js'
import decodeItineraryId from 'root-packages/airServices/decodeItineraryId.js'

const debug = debugFactory('booking-services:browser')

export default class QuoteCriteria {
  constructor ({departure, config, api} = {}) {
    const paymentSchedulePolicies = {}

    Object.entries(departure.paymentPolicies || {}).forEach(([productCode, policy]) => {
      paymentSchedulePolicies[productCode] = new PaymentSchedulePolicy(policy)
    })
    this.api = api
    this.paymentSchedule = paymentSchedulePolicies
    this.additionalTour = {}
    this.promotions = []
    this.pickupLocation = undefined
    this.referral = {}
    this.priceEngine = createPriceEngine({
      departure: departure,
      insuranceName: config.insurance ? config.insurance.text : undefined,
      paymentSchedulePolicies,
      canFetchPaymentSchedules: true,
    })

    if (typeof departure.wasPrices !== 'undefined') {
      const {discounts, supplements} = departure.wasPrices.adjustments
      this.priceEngineWasPrices = createPriceEngine({
        departure: Object.assign({}, departure, {
          prices: Object.assign({}, departure.prices, {
            baseRoomPrice: departure.wasPrices.baseRoomPrice,
            adjustments: Object.assign({}, departure.prices.adjustments, {
              discounts: Object.assign({}, departure.prices.adjustments.discounts, {
                roomType: discounts.roomType,
              }),
              supplements: Object.assign({}, departure.prices.adjustments.supplements, {
                roomType: supplements.roomType,
              }),
            }),
          }),
        }),
        insuranceName: config.insurance ? config.insurance.text : undefined,
      })
    }

    this.departure = departure
    this.config = config
    this.roomPermutations = new CachedRoomPermutations(
      this.departure.accommodation,
      this.priceEngine,
      this.config
    )
    this.passengers = []
    this.frequentTravellerDiscount = false
    this.pastGuestBenefit = false
    this.priceCache = simpleCache()
    this.discounts = {}
    this.departureCode = this.departure.code
    this.additionalTourRequested = false
    this.selectedRoomAllocation = {
      rooms: [],
    }
    this.hotelsBefore = new Hotels({
      departure: this.departure,
      beforeAfter: 'before',
      features: this.config.features,
    })
    this.hotelsAfter = new Hotels({
      departure: this.departure,
      beforeAfter: 'after',
      features: this.config.features,
    })
  }

  hotelsNowHaveAvailabilities () {
    this.hotelsAfter.hotelsNowHaveAvailabilities()
    this.hotelsBefore.hotelsNowHaveAvailabilities()
  }

  acceptUncombinedProspectivePromotion ({uncombinableProspectivePromotion, appliedPromosNotCompatibleWithNewPromotion}) {
    if (uncombinableProspectivePromotion.epd) {
      this.removeExistingNonEPDCombinablePromos()
      this.discounts.epd = true
    } else {
      const promotions = [...this.promotions]
      this.removeExistingUncombinablePromotions(
        uncombinableProspectivePromotion,
        appliedPromosNotCompatibleWithNewPromotion
      )
      this.promotions.push(uncombinableProspectivePromotion)
      const removedPromotions = promotions.filter(({code}) => !this.promotions.find(({code2}) => code === code2))
      const advocatePromo = removedPromotions.find(({promotionSubtype}) => promotionSubtype === 'ADV')
      if(advocatePromo) {
        this.clearAdvocatePromoData(advocatePromo)
      }
    }
  }

  removeExistingNonEPDCombinablePromos () {
    this.promotions = this.promotions.filter(p => p.epdCombinable)
  }

  removeReferralPromo () {
    this.promotions = this.promotions.filter(p => !['REF'].includes(p.promotionSubtype))
  }

  removeAdvocatePromo () {
    this.promotions = this.promotions.filter(p => !['ADV'].includes(p.promotionSubtype))
    this.referral.passengerSelectedForPromotion = null
  }

  removeExistingUncombinablePromotions (
    promotion,
    uncombinableAppliedPromotions
  ) {
    if (!promotion.epdCombinable) {
      this.discounts.epd = false
      this.promotions = this.promotions.filter(p => !p.epd)
    }
    this.promotions = this.promotions.filter(
      promo =>
        uncombinableAppliedPromotions.map(p => p.code).indexOf(promo.code) == -1
    )
  }

  promotionAlreadyAdded (promotion) {
    return this.promotions.filter(p => p.code == promotion.code).length > 0
  }

  productForPromotionAddedToBooking (promotion) {
    return !this.priceEngine.productNotAddedForRequestedPromotion(
      promotion,
      this.passengers,
      this.priceBreakdown().transfers,
      this.priceBreakdown().extraStays,
      this.departure
    )
  }

  addPromotion (promotion) {
    if (!this.productForPromotionAddedToBooking(promotion)) {
      return {
        productsForPromotionNotAdded: promotion.products,
      }
    } else if (!this.promotionAlreadyAdded(promotion)) {
      const breakdown = this.priceBreakdown()
      if (!promotion.amount) {
        promotion.amount = this.priceEngine.pricePromotion(
          promotion,
          this.priceBreakdown().passengers,
          {
            extraStays: breakdown.extraStays,
            flights: breakdown.flights,
            transfers: breakdown.transfers,
          }
        ).amount
      }
      const appliedEpd = breakdown.discounts.promotions.applied.find(
        p => p.epd
      )
      const appliedPromosNotCompatibleWithNewPromotion = this.priceEngine.promotionsNotCombinableWith(
        this.priceBreakdown().discounts.promotions.applied,
        promotion,
        appliedEpd
      )

      if (appliedPromosNotCompatibleWithNewPromotion.length == 0) {
        if (promotion.epd) {
          this.discounts['epd'] = true
        } else {
          this.promotions.push(promotion)
        }
      } else {
        if (promotion.automatic) {
          this.removeExistingUncombinablePromotions(
            promotion,
            appliedPromosNotCompatibleWithNewPromotion
          )
          this.promotions.push(promotion)

          return {
            promosReplacedByAutomaticPromotion: appliedPromosNotCompatibleWithNewPromotion,
          }
        } else if (appliedPromosNotCompatibleWithNewPromotion.find(p => p.automatic)) {
          this.promotionInvalidDueToAutomaticPromotion = true
        } else {
          return {
            uncombinableProspectivePromotion: clone(promotion),
            appliedPromosNotCompatibleWithNewPromotion: appliedPromosNotCompatibleWithNewPromotion,
          }
        }
      }
    }
  }

  setParty (party, selectedRooms) {
    const oldAdultsAndChildren = splitAdultsAndChildren(this.passengers)

    if (this.passengers.length) {
      const children = this.passengers.filter(passenger => passenger.type === 'child')
      children.forEach((child, index) => {
        if (child.dateOfBirth) {
          party.childAges[index] = moment(this.departure.startDate.date).diff(
            parseUserSuppliedDate(
              child.dateOfBirth,
              this.config.dateFormat
            ),
            'years')
          party.childrenDoBs[index] = child.dateOfBirth
        }
      })
      party.childAges = party && party.childAges && party.childAges.slice(0, party.children)
      party.childrenDoBs = party && party.childrenDoBs && party.childrenDoBs.slice(0, party.children)
    }

    const adultsAndChildren = expandPassengers(party, this.config)

    mergePassengers(oldAdultsAndChildren.adults, adultsAndChildren.adults)
    mergePassengers(oldAdultsAndChildren.children, adultsAndChildren.children)

    this.passengers = oldAdultsAndChildren.adults.concat(
      oldAdultsAndChildren.children
    )

    this.passengers.forEach((p, i) => {
      p.passengerId = i + 1
    })

    this.leadPassenger = this.passengers[0]
    this.party = party

    const partyForAllocation = buildPartyForAllocation({
      party: this.party,
      passengers: this.passengers,
      features: this.config.features,
      departureStartDate: this.departure.startDate,
    })

    var roomAllocations = this.roomPermutations.bestRoomAllocations(partyForAllocation)
    this.noRoomsAvailable = !roomAllocations.length

    this.roomAllocations = roomAllocations

    this.selectedRoomAllocation = this.roomPermutations.findRoomAllocationOrReturnDefault(
      roomAllocations,
      selectedRooms
    )

    if (this.flights && this.flights.selected) {
      this.flights.showFlightsRemovedForPartyWarning = true
      this.flights.clear()
    }

    if (this.promisedFlights && this.promisedFlights.selected) {
      this.promisedFlights.recalculateFlightCost()
    }

    if (this.selectedRoomAllocation) {
      var preferredRooms = this.selectedRoomAllocation.rooms
      this.hotelsBefore.setPreferredRooms(preferredRooms)
      this.hotelsBefore.setParty(party, partyForAllocation)

      this.hotelsAfter.setPreferredRooms(preferredRooms)
      this.hotelsAfter.setParty(party, partyForAllocation)
    }
  }

  selectRoomAllocation (roomAllocation) {
    var preferredRooms = roomAllocation.rooms
    this.selectedRoomAllocation = roomAllocation
    this.hotelsBefore.setPreferredRooms(preferredRooms)
    this.hotelsAfter.setPreferredRooms(preferredRooms)
  }

  async setFlightTrip (flightTripId, departureTransferCode, arrivalTransferCode) {
    if (flightTripId && !this.flights.selected) {
      const [{departDate}] = decodeItineraryId(flightTripId).slice(-1)
      const party = calculateAirfareParty(this.passengers, departDate)

      const {body} = await this.api.findFlight({
        party,
        sellingCompanyCode: this.departure.sellingCompanyCode,
        flightItineraryId: flightTripId,
        showPriceBreakdown: true,
      })
      return this.flights.select(body.flights, [
        departureTransferCode,
        arrivalTransferCode,
      ])
    }
  }

  adultPassengers () {
    return this.passengers.filter(p => p.type == 'adult')
  }

  childPassengers () {
    return this.passengers.filter(p => p.type == 'child')
  }

  popularParties () {
    return [
      {adults: 1, children: 0, childAges: [], childrenDoBs: []},
      {adults: 2, children: 0, childAges: [], childrenDoBs: []},
    ].filter(popular => {
      return this.areRoomsAvailableForParty(popular)
    })
  }

  setUrlParams (params) {
    var party = this.buildNewParty(params)

    this.setParty(party, params.rooms)
    if (params.travelProtection) {
      params.travelProtection.forEach((p, i) => {
        this.passengers[i].travelProtection = p
      })
    }

    if (params.ontarioResident) {
      params.ontarioResident.forEach((_, i) => {
        this.passengers[i].ontarioResident = true
      })
    }

    if (params.prepaidGratuities) {
      params.prepaidGratuities.forEach((p, i) => {
        this.passengers[i].prepaidGratuities = p
      })
    }
    if (params.frequentTravellerDiscount) {
      params.frequentTravellerDiscount.forEach((p, i) => {
        this.passengers[i].frequentTravellerDiscount = p
      })
    }

    try {
      if (params.hotelBefore) {
        this.hotelsBefore.setUrlParams(params.hotelBefore)
      }
    }
    catch (err) {
      console.error(err)
    }
    try {
      if (params.hotelAfter) {
        this.hotelsAfter.setUrlParams(params.hotelAfter)
      }
    }
    catch (err) {
      console.error(err)
    }
    if (params.pickupLocation) {
      this.pickupLocation = this.departure.prices.additionalProducts.find(product  => product.productCode === params.pickupLocation)
    }
    this.discounts.epd = params.epd === undefined || parseBoolean(params.epd)
    this.lmdRemoved = parseBoolean(params.lmdRemoved)
    this.ExternalRef = params.ExternalRef
  }

  buildNewParty (options) {
    if (options.adults) {
      var party = {
        adults: options.adults,
        children: options.children,
        childAges: options.childAges,
        childrenDoBs: options.childrenDoBs,
      }
      return this.areRoomsAvailableForParty(party)
        ? party
        : this.popularParties()[0]
    } else {
      return this.popularParties()[0]
    }
  }

  setAvailableTravelProtection (breakdown) {
    const breakdownPassengers = breakdown.rooms.flatMap(r => r.passengers)

    if (this.passengers.length === breakdownPassengers.length) {
      this.passengers.forEach(passenger => {
        const breakdownPassenger = breakdownPassengers.find(p => p.passengerId === passenger.passengerId)
        passenger.travelProtectionPlan = breakdownPassenger.travelProtectionPlan
        passenger.travelProtectionPlanPlus = breakdownPassenger.travelProtectionPlanPlus
        passenger.requestedTravelProtection = breakdownPassenger.requestedTravelProtection
      })
    }
  }

  areRoomsAvailableForParty (party) {
    return this.roomPermutations.areRoomsAvailableForParty(
      buildPartyForAllocation({
        party,
        passengers: this.passengers,
        features: this.config.features,
        departureStartDate: this.departure.startDate,
      })
    )
  }

  pricePromotion (promotion, breakdown) {
    return this.priceEngine.pricePromotion(promotion, breakdown.passengers, {
      promotions: this.promotions,
      flights: this.flights.selected,
      transfers: this.flights.transfers.selected,
      extraStays: breakdown.extraStays,
    })
  }

  prepareBreakdown ({priceEngine, priceCacheKey} = {priceEngine: this.priceEngine, priceCacheKey: ''}) {
    const priceOptions = this.priceOptions()
    priceOptions.config = this.config

    const childAges = clone(priceOptions.party.childAges || [])
    priceOptions.rooms.forEach(r => {
      r.passengers.filter(p => p.type == 'child').forEach(child => {
        child.age = childAges.pop()
      })
    })
    const breakdownCacheKey =
      priceCacheKey +
      JSON.stringify(this.priceOptionsWithoutPersonalDetails()) +
      ':automaticPromos:' +
      this.promotions
        .filter(p => p.automatic || p.isAirPromotion)
        .map(p => p.code)
        .join(':')

    return this.priceCache.cacheBy(breakdownCacheKey, () => {
      debug('quote criteria', priceOptions)
      const price = priceEngine.price(priceOptions, {
        promotions: this.promotions,
        flights: this.flights.selected,
        promisedFlights: this.promisedFlights.selected,
        transfers: this.flights.transfers.selected,
        features: this.config.features,
        passengers: this.passengers,
      })
      debug('price', price)
      return price
    })
  }

  getDeposit () {
    const priceBreakdown = this.priceBreakdown() || {}
    let paymentSchedule = priceBreakdown.paymentSchedule || []
    if (paymentSchedule.length > 0) {
      const clock = {getDate: () => new Date()}
      paymentSchedule = combinePaymentSchedule({paymentSchedule, departureDate: this.departure.startDate, clock})
    }

    const deposit = paymentSchedule.find(price => price.paymentType === 'Deposit')
    return deposit
  }

  wasPriceBreakdown () {
    if (!this.priceEngineWasPrices) return
    if (!this.party) return

    return this.prepareBreakdown({priceEngine: this.priceEngineWasPrices, priceCacheKey: 'was'})
  }

  priceBreakdown () {
    if (!this.party) return

    const priceBreakdown = this.prepareBreakdown({priceEngine: this.priceEngine})
    this.setAvailableTravelProtection(priceBreakdown)
    return priceBreakdown
  }

  priceOptionsWithoutPersonalDetails () {
    var clonedOptions = clone(this.priceOptions())
    clonedOptions.rooms = clonedOptions.rooms.map(room => {
      return {
        roomType: room.roomType,
        passengers: room.passengers.map(passenger => {
          return {
            type: passenger.type,
            applyAdvocatePromo: passenger.applyAdvocatePromo,
            travelProtection: passenger.travelProtection,
            prepaidGratuities: passenger.prepaidGratuities,
            pastPassengerDiscount: passenger.frequentTravellerDiscount,
            pastGuestBenefit: passenger.pastGuestBenefit,
            ontarioResident: passenger.ontarioResident,
          }
        }),
      }
    })
    return clonedOptions
  }

  getSelectedTransferData (transferType) {
    return this.flights.transfers.selected.find(transfer => transfer.type === transferType)
  }

  priceOptions () {
    const arrivalTransfer = this.getSelectedTransferData('arrival')
    const departureTransfer = this.getSelectedTransferData('departure')
    const flightsCallback = this.createFlightCallback(this.flights.requestData, this.flights.flights && this.flights.flights.callback)

    return {
      rooms: this.expandRooms().map(room => ({
        roomType: room.roomType,
        adults: room.adults,
        children: room.children,
        passengers: room.passengers.map(passenger => {
          const dateOfBirth = passenger.dateOfBirth && parseUserSuppliedDate(
            passenger.dateOfBirth,
            this.config.dateFormat
          )

          const frequentTravellerDiscount =
            passenger.frequentTravellerDiscount && passenger.frequentTravellerDiscount.selected
              ? passenger.frequentTravellerDiscount
              : undefined

          const pastGuestBenefit =
            passenger.pastGuestBenefit && passenger.pastGuestBenefit.selected
              ? passenger.pastGuestBenefit
              : undefined

          return {
            passengerId: passenger.passengerId,
            age: passenger.age,
            type: passenger.type,
            travelProtection: passenger.travelProtection,
            prepaidGratuities: passenger.prepaidGratuities,
            applyAdvocatePromo: passenger.applyAdvocatePromo,
            frequentTravellerDiscount,
            pastGuestBenefit,
            specialRequests: passenger.specialRequirements
              ? passenger.specialRequests
              : undefined,
            title: passenger.title,
            firstName: trim(passenger.firstName),
            middleName: trim(passenger.middleName),
            lastName: trim(passenger.lastName),
            dateOfBirth: dateOfBirth ? LocalDate.fromDate(dateOfBirth) : undefined,
            address: {
              line1: trim(passenger.address.line1),
              line2: trim(passenger.address.line2),
              city: trim(passenger.address.city),
              postcode: trim(passenger.address.postcode),
              country: trim(passenger.address.country),
              region: trim(passenger.address.region),
            },
            phoneNumber: {
              number: passenger.phoneNumber.number,
              internationalCode: passenger.phoneNumber.internationalCode,
              areaCode: passenger.phoneNumber.areaCode,
            },
            emailAddress: trim(passenger.emailAddress),
            gender: passenger.gender,
            ontarioResident: passenger.ontarioResident,
            smsInitialTransactionalConsent: passenger.smsInitialTransactionalConsent,
          }
        }),
      })),
      discounts: {
        epd: this.discounts.epd,
        promotions: this.promotions
          .filter(p => !p.automatic && !p.isAirPromotion)
          .map(promotion => ({
            code: promotion.code,
            notesAccepted: promotion.notesAccepted,
            inlineOffer: promotion.inlineOffer ? true : undefined,
          })),
      },
      hotelBefore: this.hotelsBefore.getUrlParams(),
      hotelAfter: this.hotelsAfter.getUrlParams(),
      party: this.party,
      flights: this.flights.selected || flightsCallback
        ? {
          itineraryId: this.flights.selected && this.flights.selected.id,
          callback: flightsCallback,
        }
        : undefined,
      promisedFlights: this.promisedFlights.selected
        ? this.promisedFlights.selected
        : undefined,
      additionalTour: {
        name: this.additionalTour.name,
        date: this.additionalTour.date,
      },
      additionalTourRequested: this.additionalTourRequested,
      transfers: {
        arrival: arrivalTransfer,
        departure: departureTransfer,
      },
      hotelPickup: this.pickupLocation,
    }
  }

  createFlightCallback (flights, callBackRequest = false) {
    if (
      flights &&
      flights.isValid() &&
      flights.type === 'explore' &&
      callBackRequest &&
      !flights.id
    ) {
      if(flights.multiCity){
        return flights.multiCity.map(flight => {
          return {
            departureAirport: flight.departureAirport,
            arriveAirport: flight.arriveAirport,
            from: flight.departureDate,
          }
        })
      }
      return {
        departureAirport: flights.departureAirport,
        from: flights.getDepartDate(),
        to: flights.getReturnDate(),
      }
    }
  }

  hasFlights () {
    return Boolean(this.flights && this.flights.selected && this.flights.selected.id)
  }

  hasPromisedFlights () {
    return Boolean(this.promisedFlights && this.promisedFlights.selected)
  }

  hasTravelProtection () {
    return this.passengers.some(passenger => passenger.travelProtection)
  }

  isTravelProtectionAvailable () {
    return this.passengers.some(passenger => passenger.travelProtectionPlan || passenger.travelProtectionPlanPlus)
  }

  allPassengersHaveMiddleNames () {
    return (
      this.passengers.map(p => p.middleName).filter(p => p && p.trim())
        .length === this.passengers.length
    )
  }

  expandRooms () {
    var passengersLeft = this.passengers.slice()
    function pickPassengers (type, number) {
      return filterSplice(passengersLeft, number, p => p.type == type)
    }

    var expandedRooms = []
    if (this.selectedRoomAllocation &&  this.selectedRoomAllocation.rooms) {
      this.selectedRoomAllocation.rooms.forEach(room => {
        for (var i = 0; i < room.count; i++) {
          expandedRooms.push({
            roomType: room.roomType,
            passengers: pickPassengers('adult', room.adults).concat(
              pickPassengers('child', room.children)
            ),
            adults: room.adults,
            children: room.children,
          })
        }
      })
    }
    return expandedRooms
  }

  removeAirPromotions () {
    const promosToDelete = this.promotions
      .filter(p => p.isAirPromotion)
      .map(p => p.code)
    this.promotions.reduceRight((memo, promo, i) => {
      if (promosToDelete.indexOf(promo.code) > -1) this.promotions.splice(i, 1)
    }, 0)
  }

  removeDiscount (discountType, promotionCode) {
    if (discountType == 'epd') {
      this.discounts['epd'] = false
    } else if (discountType == 'promotion') {
      const index = this.promotions.findIndex(p => p.code == promotionCode)
      if (index !== -1) {
        const promoToRemove = this.promotions[index]
        this.clearAdvocatePromoData(promoToRemove)
        this.promotions.splice(index, 1)
      }
    }
  }

  clearAdvocatePromoData (promotion) {
    if(promotion.promotionSubtype === 'ADV') {
      this.referral.passengerSelectedForPromotion = null
      this.referral.creditsToRedeem = 0
    }
  }

  clearHotelsBefore () {
    this.hotelsBefore.chosenHotel.hotelId = undefined
    this.hotelsBefore.selectedNights.chosenHotel.hotelId = undefined
  }

  clearHotelsAfter () {
    this.hotelsAfter.chosenHotel.hotelId = undefined
    this.hotelsAfter.selectedNights.chosenHotel.hotelId = undefined
  }

  hasSelectedHotels () {
    return this.hotelsBefore.nights + this.hotelsAfter.nights > 0
  }

  extraNightsChanged () {
    if (this.hasFlights()) {
      this.flights.showFlightsRemovedForExtraNightsWarning = true
      this.flights.clear()
    }

    if (this.hasPromisedFlights()) {
      this.promisedFlights.addExtraNightsWarning = true
    }
  }
}

function splitAdultsAndChildren (passengers) {
  return {
    adults: passengers.filter(p => p.type == 'adult'),
    children: passengers.filter(p => p.type == 'child'),
  }
}

function mergePassengers (oldPassengers, passengers) {
  if (oldPassengers.length < passengers.length) {
    oldPassengers.push(...passengers.slice(oldPassengers.length))
  } else if (oldPassengers.length > passengers.length) {
    oldPassengers.splice(
      passengers.length,
      oldPassengers.length - passengers.length
    )
  }

  zip(oldPassengers, passengers).map(([old, _new]) => {
    if (old.age != _new.age) {
      old.age = _new.age
    }
  })
}

function expandPassengers (party, config) {
  return {
    adults: range(party.adults).map(i => {
      var passenger = new Passenger('adult', 'Passenger ' + (i + 1))

      if (i == 0) {
        passenger.setLeadPassengerCountryDefaults(config, config.features.clientAddressMandatory)
      }

      return passenger
    }),

    children: range(party.children).map((i) => {
      const age = party.childAges && party.childAges[i]
      const dateOfBirth = party.childrenDoBs && party.childrenDoBs[i]
      return new Passenger(
        'child',
        'Passenger ' + (i + 1 + party.adults),
        age,
        dateOfBirth
      )
    }),
  }
}

class Passenger {
  constructor (type, name, age, dateOfBirth) {
    this.type = type
    this.name = name
    this.address = {}
    this.phoneNumber = {}
    this.age = age
    this.frequentTravellerDiscount = {}
    this.pastGuestBenefit = {}
    this.dateOfBirth = dateOfBirth
    this.applyAdvocatePromo = false
  }

  fullName () {
    return this.firstName ? `${this.firstName} ${this.lastName}` : this.name
  }

  setLeadPassengerCountryDefaults (config, setupCountry) {
    this.phoneNumber = {
      countryCode: config.countryCode,
    }
    this.mobileNumber = {
      countryCode: config.countryCode,
    }
    if (setupCountry) {
      this.address = {
        country: config.country,
      }
    }
  }
}

function parseBoolean (s) {
  if (typeof s == 'string') {
    return s === 'true'
  } else {
    return !!s
  }
}

function trim (input) {
  return input ? input.trim() : undefined
}
